// Rung 4 — asking someone you do not employ.
//
// An offer is the one artifact on this channel that is public and
// unsealed by design, and it is deliberately **not** the job with the key
// removed. A job minus its key is still every stop coordinate, every
// address label, every order reference and every customer's phone
// number — broadcasting that to a pool of bidders would disclose more by
// losing bids than by running deliveries.
//
// So what goes out is a commitment plus four coarse descriptors: how many
// drops, roughly where (a 0.01° grid cell, about a kilometre), roughly
// how far it ranges (one of five buckets), and exactly how long it is,
// because the size of the work is not a location. This screen shows the
// operator both columns of that table before they press the button.

import { useCallback, useMemo, useState } from "react";
import { Copyable, Field, Note, Panel, Qr } from "./Bits";
import { type Dispatcher } from "../ops/dispatch";
import { localId, type FleetState, type StoredJob, type StoredOffer } from "../ops/state";
import { clockWords, distanceWords, plural, type Vocabulary } from "../ops/words";
import { nowSeconds } from "../config";

export function Offers({
  state,
  vocabulary,
  dispatcher,
  onSaveOffer,
  onAward,
  onCollectBid
}: {
  state: FleetState;
  vocabulary: Vocabulary;
  dispatcher: Dispatcher | null;
  onSaveOffer(offer: StoredOffer): void;
  onAward(deviceId: string, job: StoredJob, driverUrl: string, sealedB64: string): void;
  onCollectBid(offer: StoredOffer): void;
}): JSX.Element {
  const open = useMemo(
    () => state.jobs.filter(job =>
      !job.heldBy
      && job.finishedUnixSeconds == null
      && !state.offers.some(offer => offer.awardedJobId === job.id || offer.jobIdHex === job.jobIdHex)),
    [state.jobs, state.offers]
  );

  return (
    <div className="stack">
      {state.offers.map(offer => (
        <OfferCard
          key={offer.id}
          offer={offer}
          state={state}
          vocabulary={vocabulary}
          dispatcher={dispatcher}
          onSaveOffer={onSaveOffer}
          onAward={onAward}
          onCollectBid={onCollectBid}
        />
      ))}

      <Panel eyebrow="Put a job out">
        {open.length ? (
          <div className="rows">
            {open.map(job => (
              <NewOffer
                key={job.id}
                job={job}
                state={state}
                vocabulary={vocabulary}
                dispatcher={dispatcher}
                onSaveOffer={onSaveOffer}
              />
            ))}
          </div>
        ) : (
          <p className="small muted">
            Nothing is waiting without a {vocabulary.driver}. When a {vocabulary.job} goes out with
            nobody free, it turns up here.
          </p>
        )}
      </Panel>
    </div>
  );
}

function NewOffer({
  job,
  state,
  vocabulary,
  dispatcher,
  onSaveOffer
}: {
  job: StoredJob;
  state: FleetState;
  vocabulary: Vocabulary;
  dispatcher: Dispatcher | null;
  onSaveOffer(offer: StoredOffer): void;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const [pay, setPay] = useState("");
  const [currency, setCurrency] = useState("EUR");
  const [label, setLabel] = useState("");
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  const broadcast = useCallback(async () => {
    if (!dispatcher) return;
    setBusy(true);
    setProblem(null);
    try {
      const ticket = await dispatcher.openTicket(job.sealedB64, state.consoleDevice.privateKeyHex);
      const issued = await dispatcher.issueOffer({
        issuerSeedHex: state.issuerSeedHex,
        ticket,
        totalMeters: job.totalMeters ?? 0,
        payMinor: pay.trim() ? Math.round(Number(pay) * 100) : null,
        currency: pay.trim() ? currency : null,
        label: label.trim() || null
      });
      onSaveOffer({
        id: localId("offer"),
        createdUnixSeconds: nowSeconds(),
        jobIdHex: issued.jobIdHex,
        url: issued.url,
        planB64: dispatcher.threads.bytesToBase64Url(ticket.planBytes),
        planKey: job.planKey,
        planRefHex: job.planRefHex,
        stops: job.stops,
        notAfter: issued.offer.notAfter,
        mode: issued.offer.mode,
        stopCount: issued.offer.stopCount,
        centroidLat: issued.offer.centroid.lat,
        centroidLon: issued.offer.centroid.lon,
        spread: issued.offer.spread,
        spreadLabel: issued.offer.spreadLabel,
        totalMeters: issued.offer.totalMeters,
        payMinor: issued.offer.payMinor,
        currency: issued.offer.currency,
        label: issued.offer.label,
        bidDeviceIds: [],
        awardedDeviceId: null,
        awardedJobId: job.id,
        awardCheck: null
      });
      setOpen(false);
    } catch (error) {
      setProblem(error instanceof Error ? error.message : String(error));
    } finally {
      setBusy(false);
    }
  }, [dispatcher, job, state.consoleDevice.privateKeyHex, state.issuerSeedHex, pay, currency, label, onSaveOffer]);

  if (!open) {
    return (
      <div className="rowcard">
        <div className="rowcard__main">
          <span className="rowcard__name">
            {plural(job.stops.length, vocabulary.stop)} · {job.stops[0]!.label}
          </span>
          <span className="rowcard__sub">
            Waiting since {clockWords(job.createdUnixSeconds)}
            {job.totalMeters ? ` · ${distanceWords(job.totalMeters)}` : ""}
          </span>
        </div>
        <button type="button" className="button button--small" onClick={() => setOpen(true)}>
          Offer it out
        </button>
      </div>
    );
  }

  return (
    <div className="panel panel--sunk">
      <div className="stack stack--tight">
        <div className="grid2">
          <Field label="Pay" hint="Optional. Whole units; it goes out as minor units.">
            <div className="row row--tight">
              <input
                className="input"
                style={{ maxWidth: "7rem" }}
                type="number"
                min={0}
                step="0.01"
                value={pay}
                onChange={event => setPay(event.target.value)}
              />
              <select className="select" style={{ maxWidth: "6rem" }} value={currency} onChange={event => setCurrency(event.target.value)}>
                <option>EUR</option>
                <option>GBP</option>
                <option>USD</option>
                <option>CAD</option>
                <option>CHF</option>
              </select>
            </div>
          </Field>
          <Field label="A public line" hint="48 bytes, and it really is public. Nothing it does not say is disclosed.">
            <input
              className="input"
              placeholder="12 parcels, city centre"
              maxLength={48}
              value={label}
              onChange={event => setLabel(event.target.value)}
            />
          </Field>
        </div>

        <Note title="What a stranger learns from this, and what they do not." tone="quiet">
          They learn how many drops there are, roughly which square kilometre the round sits in,
          which of five size buckets it ranges over, exactly how far it is, the pay if you state it,
          and the line you wrote. They do not learn a single address, a street, an order reference,
          a parcel count, an instruction, or a customer’s phone number — none of it is in the bytes.
        </Note>

        {problem ? (
          <div className="banner banner--halt" role="alert">
            <span className="banner__mark">No</span>
            <p className="banner__text">{problem}</p>
          </div>
        ) : null}

        <div className="row row--end">
          <button type="button" className="button button--small" onClick={() => setOpen(false)}>Cancel</button>
          <button
            type="button"
            className="button button--primary"
            disabled={!dispatcher || busy}
            onClick={() => { void broadcast(); }}
          >
            {busy ? "Signing…" : "Broadcast it"}
          </button>
        </div>
      </div>
    </div>
  );
}

function OfferCard({
  offer,
  state,
  vocabulary,
  dispatcher,
  onSaveOffer,
  onAward,
  onCollectBid
}: {
  offer: StoredOffer;
  state: FleetState;
  vocabulary: Vocabulary;
  dispatcher: Dispatcher | null;
  onSaveOffer(offer: StoredOffer): void;
  onAward(deviceId: string, job: StoredJob, driverUrl: string, sealedB64: string): void;
  onCollectBid(offer: StoredOffer): void;
}): JSX.Element {
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const bidders = state.devices.filter(device => offer.bidDeviceIds.includes(device.id));
  const spread = offer.spreadLabel;
  const job = state.jobs.find(entry => entry.id === offer.awardedJobId) ?? null;

  const award = useCallback(async (deviceId: string) => {
    if (!dispatcher || !job) return;
    setBusy(true);
    setProblem(null);
    try {
      const device = state.devices.find(entry => entry.id === deviceId);
      if (!device) throw new Error("That phone is not paired.");
      const resealed = await dispatcher.reseal({
        sealedB64: job.sealedB64,
        consolePrivateKeyHex: state.consoleDevice.privateKeyHex,
        recipients: [device.publicKeyHex, state.consoleDevice.publicKeyHex]
      });
      // The check the *courier's* device runs before a wheel turns, run
      // here too: it recomputes the plan hash from the awarded bytes and
      // says which check failed if one does. It holds against a
      // dispatcher who re-signs its own forgery, which is the actual
      // threat since the dispatcher owns the key.
      const verdict = dispatcher.checkAward(dispatcher.openOffer(offer.url), resealed.ticket);
      onSaveOffer({
        ...offer,
        awardedDeviceId: deviceId,
        awardCheck: verdict.ok
          ? "The awarded job is the job that was advertised."
          : `This award does not match the offer: ${verdict.reason ?? "unknown"}.`
      });
      onAward(deviceId, job, resealed.driverUrl, resealed.sealedB64);
    } catch (error) {
      setProblem(error instanceof Error ? error.message : String(error));
    } finally {
      setBusy(false);
    }
  }, [dispatcher, job, offer, state.devices, state.consoleDevice.privateKeyHex, state.consoleDevice.publicKeyHex, onSaveOffer, onAward]);

  return (
    <Panel eyebrow={offer.label ? `Out: ${offer.label}` : "Out for bids"}>
      <div className="handover handover--wide">
        <div>
          <Qr value={offer.url} alt="The job offer, as a code" />
        </div>
        <div className="stack stack--tight">
          <div className="kv">
            <span className="kv__key">Drops</span>
            <p className="kv__value"><strong>{offer.stopCount}</strong></p>
            <span className="kv__key">Roughly</span>
            <p className="kv__value">
              {offer.centroidLat.toFixed(2)}, {offer.centroidLon.toFixed(2)} — a square about a
              kilometre across, and the centre of the round rather than any doorstep
            </p>
            <span className="kv__key">Ranges</span>
            <p className="kv__value">{spread}</p>
            <span className="kv__key">Distance</span>
            <p className="kv__value"><strong>{distanceWords(offer.totalMeters)}</strong>, exactly</p>
            {offer.payMinor != null ? (
              <>
                <span className="kv__key">Pay</span>
                <p className="kv__value">
                  <strong>{(offer.payMinor / 100).toFixed(2)} {offer.currency}</strong>
                </p>
              </>
            ) : null}
            <span className="kv__key">Until</span>
            <p className="kv__value">{clockWords(offer.notAfter)}</p>
          </div>
          <Copyable label="The offer" value={offer.url} />
        </div>
      </div>

      <hr className="divider" />

      <p className="field__label">Who came back</p>
      {bidders.length ? (
        <div className="rows" style={{ marginTop: "0.35rem" }}>
          {bidders.map(device => (
            <div key={device.id} className="rowcard">
              <div className="rowcard__main">
                <span className="rowcard__name">{device.name}</span>
                <span className="rowcard__sub">Paired, so a job can be sealed to it</span>
              </div>
              <code className="fingerprint wf-code">{device.fingerprint}</code>
              {offer.awardedDeviceId === device.id ? (
                <span className="chip chip--settled"><span className="chip__dot" />Awarded</span>
              ) : (
                <button
                  type="button"
                  className="button button--small"
                  disabled={busy || !!offer.awardedDeviceId}
                  onClick={() => { void award(device.id); }}
                >
                  Award it
                </button>
              )}
            </div>
          ))}
        </div>
      ) : (
        <p className="small muted" style={{ marginTop: "0.35rem" }}>
          Nobody yet. A {vocabulary.driver} who wants it sends you their phone’s code by whatever
          you already use — that is the only channel there is, and it is deliberately not a market.
          Claims, payments and reputation all need somebody accountable, and nothing on this wire
          is.
        </p>
      )}

      <div className="row" style={{ marginTop: "0.5rem" }}>
        <button type="button" className="button button--small" onClick={() => onCollectBid(offer)}>
          Someone sent their code
        </button>
      </div>

      {offer.awardCheck ? (
        <div style={{ marginTop: "0.7rem" }}>
          <Note
            title={offer.awardedDeviceId ? "Checked before a wheel turned." : "Checked"}
            tone={offer.awardCheck.startsWith("The awarded") ? "settled" : "halt"}
          >
            {offer.awardCheck} Without that check an offer is a marketing line: advertise three
            stops downtown, award thirty across the province, and the argument is the courier’s to
            lose.
          </Note>
        </div>
      ) : null}

      {problem ? (
        <div className="banner banner--halt" role="alert" style={{ marginTop: "0.6rem" }}>
          <span className="banner__mark">No</span>
          <p className="banner__text">{problem}</p>
        </div>
      ) : null}
    </Panel>
  );
}
